import { MessageType } from "../../../types/conversationInbox";
import type { ReactionType } from "../../../types/conversationInbox";

import type {
  InboxV2ConversationMessage,
  InboxV2MessageAttachment,
  InboxV2MessageReaction,
  InboxV2Sentiment,
} from "../../types";
import type {
  InboxV2RawConvLastMessage,
  InboxV2RawMessage,
} from "../real-api/types";
import {
  isInboxV2CommentMessageKind,
  isInboxV2DirectMessageKind,
  isInboxV2MessageKindRenderable,
  isInboxV2PostSentimentMessageKind,
  isInboxV2SystemMessageKind,
} from "../../ui-meta/message-meta";
import { formatInboxV2Timestamp } from "../../utils/date-format";

import {
  InboxV2MessageMappingOptions,
  mapInboxV2Identity,
} from "./interlocutor";
import {
  isEmptyPlatformAttachmentMessage,
  mapMessageAttachments,
  mapMessageStory,
} from "./message-attachment";

export function shouldDisplayInboxV2Message(message: InboxV2RawMessage) {
  return isInboxV2MessageKindRenderable(
    getInboxV2MessageKind(message.type, {
      isInternal: shouldTreatInboxV2MessageAsInternal(message),
    })
  );
}

export function shouldStoreInboxV2Message(message: InboxV2RawMessage) {
  const kind = getInboxV2MessageKind(message.type, {
    isInternal: shouldTreatInboxV2MessageAsInternal(message),
  });

  return (
    isInboxV2MessageKindRenderable(kind) ||
    isInboxV2PostSentimentMessageKind(kind)
  );
}

function shouldTreatInboxV2MessageAsInternal(message: {
  isInternal?: boolean;
  type: MessageType | string;
}) {
  if (
    message.type === MessageType.system ||
    message.type === MessageType.typing ||
    message.type === MessageType.messageCreating
  ) {
    return false;
  }

  return Boolean(message.isInternal) || message.type === MessageType.internal;
}

export function mapMessageToInboxV2Message(
  message: InboxV2RawMessage,
  options?: InboxV2MessageMappingOptions
): InboxV2ConversationMessage {
  const sender = mapInboxV2Identity(message.sender, {
    account: options?.account,
    sentFromSked: Boolean(message.sentFromSked),
  });
  const story = mapMessageStory(message);
  const attachments = mapMessageAttachments(message);

  const baseMessage = {
    kind: getInboxV2MessageKind(message.type, {
      isInternal: shouldTreatInboxV2MessageAsInternal(message),
    }),
    sourceType: message.type,
    id: message.id,
    accountId: options?.conversationAccountId,
    conversationId: undefined,
    platformMessageId: message.platformId ?? undefined,
    authorId: sender.id,
    authorName: message.integrationAuthorName ?? sender.name,
    authorType: message.integrationAuthorType,
    authorHandle: sender.handle,
    authorPlatform: sender.platform,
    authorPlatformId: sender.platformId,
    authorFetchedError: sender.fetchedError,
    authorIsUnknownSource: sender.isUnknownSource,
    avatarUrl: sender.avatarUrl,
    createdAt: String(message.createdAt),
    createdAtLabel: formatInboxV2Timestamp(message.createdAt),
    body: message.text ?? "",
    isBrand: sender.isConversationAccountSender,
    isConversationAccountSender: sender.isConversationAccountSender,
    sentFromSked: sender.sentFromSked,
    sentFromSkedUserName:
      message.integrationAuthorType === "integration"
        ? (message.integrationAuthorName ?? "Agent")
        : sender.sentFromSkedUserName,
  };

  if (isInboxV2SystemMessageKind(baseMessage.kind)) {
    return {
      ...baseMessage,
      kind: baseMessage.kind,
      isSystem: true,
    };
  }

  if (baseMessage.kind === "message-creating") {
    return {
      ...baseMessage,
      kind: "message-creating",
      sendStatus: "sending",
      subKind: message.isInternal ? "internal" : "social",
      targetLayout: "comment",
    };
  }

  if (baseMessage.kind === "internal") {
    return {
      ...baseMessage,
      kind: "internal",
    };
  }

  if (isInboxV2DirectMessageKind(baseMessage.kind)) {
    const directAttachments: InboxV2MessageAttachment[] =
      attachments.length > 0 ||
      story ||
      !isEmptyPlatformAttachmentMessage(message)
        ? attachments
        : [{ type: "unknown", unavailable: true }];
    const directMessage = {
      ...baseMessage,
      story,
      attachments: directAttachments,
      reactions: mapMessageReactions(message, options),
      commentRef: message.commentRef,
      directReplyToMessageId: message.commentRef?.dbId,
    };

    if (baseMessage.kind === "tt-message") {
      const isUnidentifiedTikTokMessage =
        message.type === MessageType.ttMessage &&
        message.ttMessageType === "other" &&
        !baseMessage.body.trim() &&
        attachments.length === 0 &&
        !story;

      return {
        ...directMessage,
        kind: "tt-message",
        attachments: isUnidentifiedTikTokMessage
          ? attachments
          : directAttachments,
        isUnidentifiedTikTokMessage,
      };
    }

    return { ...directMessage, kind: baseMessage.kind };
  }

  if (isInboxV2CommentMessageKind(baseMessage.kind)) {
    return {
      ...baseMessage,
      kind: baseMessage.kind,
      sentiment: mapSentimentTypeToInboxV2Sentiment(message.sentiment?.type),
      isDeleted: Boolean(message.deleted),
      isHidden: Boolean(message.hidden),
      directMessageId: message.directMessageId,
      attachments,
      likeCount: message.likes,
      reactions: mapMessageReactions(message, options),
      threadedLevel: "level" in message ? message.level : undefined,
    };
  }

  if (!isInboxV2MessageKindRenderable(baseMessage.kind)) {
    return {
      ...baseMessage,
      kind: baseMessage.kind,
      sentiment: mapSentimentTypeToInboxV2Sentiment(message.sentiment?.type),
    };
  }

  return {
    ...baseMessage,
    kind: "unknown",
    sentiment: mapSentimentTypeToInboxV2Sentiment(message.sentiment?.type),
    isSystem: message.type === MessageType.system,
    isDeleted: Boolean(message.deleted),
    isHidden: Boolean(message.hidden),
    story,
    attachments,
    likeCount: message.likes,
    reactions: mapMessageReactions(message, options),
  };
}

function getInboxV2MessageKind(
  messageType: MessageType | string,
  options?: { isInternal?: boolean }
): InboxV2ConversationMessage["kind"] {
  switch (messageType) {
    case MessageType.system:
      return "system";
    case MessageType.typing:
      return "typing";
    case MessageType.messageCreating:
      return "message-creating";
  }

  if (options?.isInternal) {
    return "internal";
  }

  switch (messageType) {
    case MessageType.fbComment:
      return "fb-comment";
    case MessageType.fbThreadedComment:
      return "fb-threaded-comment";
    case MessageType.fbMentionV2:
      return "fb-mention-v2";
    case MessageType.igComment:
      return "ig-comment";
    case MessageType.igThreadedComment:
      return "ig-threaded-comment";
    case MessageType.liComment:
      return "li-comment";
    case MessageType.liThreadedComment:
      return "li-threaded-comment";
    case MessageType.ttThreadedComment:
      return "tt-threaded-comment";
    case MessageType.gmbReviewReply:
      return "gmb-review-reply";
    case MessageType.gmbAnwser:
      return "gmb-answer";
    case MessageType.igMention:
      return "ig-mention";
    case MessageType.igMentionV2:
      return "ig-mention-v2";
    case MessageType.fbMessage:
      return "fb-message";
    case MessageType.igMessage:
      return "ig-message";
    case MessageType.ttMessage:
      return "tt-message";
    case MessageType.igStoryMention:
      return "ig-story-mention";
    case MessageType.igStoryReply:
      return "ig-story-reply";
    case MessageType.fbPost:
      return "fb-post";
    case MessageType.fbMentionInPostV2:
      return "fb-mention-in-post-v2";
    case MessageType.igPost:
      return "ig-post";
    case MessageType.igMentionPost:
      return "ig-mention-post";
    case MessageType.igMentionInPostV2:
      return "ig-mention-in-post-v2";
    case MessageType.liPost:
      return "li-post";
    case MessageType.internal:
      return "internal";
    default:
      return "unknown";
  }
}

function mapMessageReactions(
  message: InboxV2RawMessage,
  options?: InboxV2MessageMappingOptions
): InboxV2MessageReaction[] {
  if (message.type === MessageType.ttMessage) {
    return (message.reaction ?? []).map((reaction) => ({
      id: `${message.id}:${reaction.unique_identifier}`,
      reactee: reaction.unique_identifier,
      reaction: "other",
      emoji: reaction.emoji,
      createdAt: new Date(reaction.timestamp).toISOString(),
    }));
  }

  if (message.type === MessageType.ttThreadedComment) {
    return message.liked
      ? [
          {
            id: message.id,
            reactee: message.accountId ?? options?.conversationAccountId ?? "",
            reaction: "love",
            createdAt: String(message.createdAt),
          },
        ]
      : [];
  }

  return (message.reactions ?? []).map((element) =>
    mapReactionToInboxV2Reaction(element)
  );
}

export function mapReactionToInboxV2Reaction(
  reaction: ReactionType
): InboxV2MessageReaction {
  return {
    id: reaction.id,
    reactee: reaction.reactee,
    reaction:
      reaction.reaction as unknown as InboxV2MessageReaction["reaction"],
    emoji: reaction.emoji,
    createdAt: reaction.createdAt,
  };
}

export function mapLastMessageToInboxV2Message(
  message: InboxV2RawConvLastMessage | undefined,
  conversationId: string,
  options?: InboxV2MessageMappingOptions
): InboxV2ConversationMessage | undefined {
  if (!message) {
    return undefined;
  }

  const sender = mapInboxV2Identity(message.sender, {
    account: options?.account,
  });
  const kind = getInboxV2MessageKind(message.type, {
    isInternal: shouldTreatInboxV2MessageAsInternal(message),
  });

  const baseMessage = {
    kind,
    sourceType: message.type,
    id: message.id,
    accountId: options?.conversationAccountId,
    conversationId,
    platformMessageId: message.platformId ?? undefined,
    authorName: sender.name,
    authorHandle: sender.handle,
    authorPlatform: sender.platform,
    authorPlatformId: sender.platformId,
    avatarUrl: sender.avatarUrl,
    createdAt: String(message.createdAt),
    createdAtLabel: formatInboxV2Timestamp(message.createdAt),
    body: message.text ?? "",
    isBrand: sender.isConversationAccountSender,
    isConversationAccountSender: sender.isConversationAccountSender,
    sentFromSked: sender.sentFromSked,
    sentFromSkedUserName: sender.sentFromSkedUserName,
  };

  if (isInboxV2CommentMessageKind(kind)) {
    return {
      ...baseMessage,
      kind,
      sentiment: "unknown",
      isDeleted: Boolean(message.deleted),
      reactions: [],
    };
  }

  if (isInboxV2DirectMessageKind(kind)) {
    return {
      ...baseMessage,
      kind,
      reactions: [],
    };
  }

  if (kind === "internal") {
    return {
      ...baseMessage,
      kind: "internal",
    };
  }

  if (isInboxV2SystemMessageKind(kind)) {
    return {
      ...baseMessage,
      kind,
      isSystem: true,
    };
  }

  if (kind === "message-creating") {
    return {
      ...baseMessage,
      kind: "message-creating",
      sendStatus: "sending",
      subKind: shouldTreatInboxV2MessageAsInternal(message)
        ? "internal"
        : "social",
      targetLayout: "comment",
    };
  }

  if (!isInboxV2MessageKindRenderable(kind)) {
    return {
      ...baseMessage,
      kind,
    };
  }

  return {
    ...baseMessage,
    kind: "unknown",
    sentiment: "unknown",
    isSystem: message.type === MessageType.system,
    isDeleted: Boolean(message.deleted),
    reactions: [],
  };
}

export function mapSentimentTypeToInboxV2Sentiment(
  sentimentType?: number
): InboxV2Sentiment {
  switch (sentimentType) {
    case 1: {
      return "positive";
    }
    case 2: {
      return "negative";
    }
    case 3: {
      return "neutral";
    }
    case 4: {
      return "mixed";
    }
    case 5:
    default: {
      return "unknown";
    }
  }
}
